import { useCallback, useState } from 'react';
import type { BrushOptions } from '@design-ai/gpu-core';
import { ExportPanel } from './components/ExportPanel.js';
import { MattePanel } from './components/MattePanel.js';
import { ModelPanel } from './components/ModelPanel.js';
import { RedesignPanel } from './components/RedesignPanel.js';
import { UpscalePanel } from './components/UpscalePanel.js';
import { Viewport, type Tool } from './components/Viewport.js';
import { useEngine } from './hooks/useEngine.js';
import { useImageInput, type IncomingImage } from './hooks/useImageInput.js';

export function App() {
  const { support, engine, error } = useEngine();
  const [version, setVersion] = useState(0);
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>('pan');
  const [brush, setBrush] = useState<BrushOptions>({ radius: 24, hardness: 0.6, flow: 1 });
  const [showAlpha, setShowAlpha] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mattingError, setMattingError] = useState<string | null>(null);

  const bump = useCallback(() => setVersion((n) => n + 1), []);

  const onImage = useCallback(
    (image: IncomingImage) => {
      if (!engine) return;
      engine.workbench.loadSource(image.bitmap, image.width, image.height);
      image.bitmap.close();
      setSourceLabel(`${image.label} - ${image.width} x ${image.height}`);
      setMattingError(null);
      setTool('pan');
      bump();
    },
    [engine, bump],
  );

  const { dragging, openFilePicker } = useImageInput(onImage);

  const runMatting = useCallback(async () => {
    if (!engine) return;
    setBusy(true);
    setMattingError(null);
    try {
      await engine.workbench.runMatting();
      bump();
    } catch (cause) {
      setMattingError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }, [engine, bump]);

  if (!support.supported) {
    return <Fallback title="WebGPU is required" detail={support.reason ?? ''} />;
  }
  if (error) {
    return <Fallback title="Could not start the GPU engine" detail={error} />;
  }
  if (!engine) {
    return <Fallback title="Starting WebGPU..." detail="Requesting an adapter and device." />;
  }

  const state = engine.workbench.state;

  return (
    <div className={`app${dragging ? ' app--dragging' : ''}`}>
      <header className="app__header">
        <div>
          <h1>Design AI</h1>
          <p className="app__tagline">
            Paste a mockup crop, cut it out, clean it up, upscale it - all locally. Only the
            redesign step sends anything out.
          </p>
        </div>
        <div className="app__source">
          {sourceLabel ? <span>{sourceLabel}</span> : <span className="muted">No image yet</span>}
          <button type="button" className="button" onClick={openFilePicker}>
            Open file
          </button>
        </div>
      </header>

      <main className="app__main">
        {state.stage === 'empty' ? (
          <div className="dropzone">
            <h2>Paste an image</h2>
            <p>
              Crop a design on Amazon or Etsy with the extension, then press <kbd>Ctrl</kbd>+
              <kbd>V</kbd> here. Drag and drop and the file picker work too.
            </p>
            <p className="muted">
              GPU: {engine.ctx.caps.vendor} {engine.ctx.caps.architecture}
              {engine.ctx.caps.shaderF16 ? ' - fp16 supported' : ''}
            </p>
          </div>
        ) : (
          <Viewport
            engine={engine}
            tool={tool}
            brush={brush}
            showAlpha={showAlpha}
            version={version}
            onPainted={bump}
          />
        )}

        <aside className="app__sidebar">
          <ModelPanel engine={engine} onLoaded={bump} />
          {mattingError ? <p className="error">{mattingError}</p> : null}
          <MattePanel
            engine={engine}
            ready={engine.workbench.hasMattingModel && state.stage !== 'empty'}
            busy={busy}
            tool={tool}
            brush={brush}
            showAlpha={showAlpha}
            onToolChange={setTool}
            onBrushChange={setBrush}
            onShowAlphaChange={setShowAlpha}
            onRunMatting={() => void runMatting()}
            onRefine={bump}
          />
          <UpscalePanel engine={engine} ready={engine.workbench.hasUpscaleModel} onDone={bump} />
          <ExportPanel engine={engine} ready={state.stage !== 'empty'} />
          <RedesignPanel
            engine={engine}
            ready={state.stage !== 'empty'}
            onAdopted={() => {
              setSourceLabel('redesign result');
              setTool('pan');
              bump();
            }}
          />
        </aside>
      </main>
    </div>
  );
}

function Fallback({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="fallback">
      <h1>{title}</h1>
      <p>{detail}</p>
    </div>
  );
}
